/**
 * CookieYes HSKI UI:
 * - clamp opisu do 3 linii + Rozwiń/Zwiń (desktop + expanded mobile),
 * - mobile (≤1023px): FAB zamiast pełnego banera (nie zasłania sticky bara),
 * - tap FAB → pełny .cky-consent-container.
 * Style: cookieyes.css (sync w head). Baner asynchroniczny — retry + wąski MutationObserver.
 *
 * UWAGA: nie obserwować attributeFilter style/class na całym documentElement —
 * Header (scroll/nav-hide/--nav-height-js) + animacje CookieYes powodowały
 * pętlę MO → run() → setProperty/classList → MO (freeze home).
 */

const ui = (msgid) => {
  const text = String(msgid ?? '');
  if (!text) return '';
  const bag = typeof window !== 'undefined' ? window.hskiI18n?.msg : null;
  const value = bag && typeof bag[text] === 'string' ? bag[text] : '';
  return value || text;
};

const LABEL_EXPAND = () => ui('Rozwiń');
const LABEL_COLLAPSE = () => ui('Zwiń');
const ARIA_EXPAND = () => ui('Rozwiń opis zgody na pliki cookie');
const ARIA_COLLAPSE = () => ui('Zwiń opis zgody na pliki cookie');
const ARIA_FAB = () => ui('Ustawienia plików cookie');

/** Zgodne z stickySummaryBar STICKY_SUMMARY_MQ — sticky bar jest w tym zakresie. */
const MOBILE_MQ = '(max-width: 1023px)';

const CLS_MIN = 'hski-cky-mobile-min';
const CLS_OPEN = 'hski-cky-mobile-open';
const CLS_DONE = 'hski-cky-done';

const descEl = (root) =>
  (root || document).querySelector(
    '.cky-consent-bar .cky-notice-des, .cky-consent-bar [data-cky-tag="description"]',
  );

const consentContainer = () =>
  document.querySelector('.cky-consent-container:not(.cky-hide)');

const isShown = (el) => {
  if (!el) return false;
  const cs = window.getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  return el.getClientRects().length > 0;
};

const isMobile = () => window.matchMedia(MOBILE_MQ).matches;

const consentCompleted = () => {
  try {
    if (typeof window.getCkyConsent === 'function') {
      const c = window.getCkyConsent();
      if (c && c.isUserActionCompleted) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

/** @returns {boolean|null} null = jeszcze nie da się zmierzyć */
const needsClamp = (el) => {
  if (!el || !isShown(el)) return null;
  el.classList.remove('is-expanded');
  el.classList.add('hski-cky-clamp');
  const clamped = el.getBoundingClientRect().height;
  el.classList.add('is-expanded');
  const full = el.scrollHeight;
  el.classList.remove('is-expanded');
  return full > clamped + 2;
};

const ensureExpand = (desc) => {
  if (!desc || desc.dataset.hskiCkyBound === '1') return true;
  const container = desc.closest('.cky-consent-container');
  if (container?.classList.contains('cky-hide')) return false;
  if (!isShown(desc)) return false;

  const need = needsClamp(desc);
  if (need === null) return false;
  desc.dataset.hskiCkyBound = '1';
  if (!need) {
    desc.classList.remove('hski-cky-clamp', 'is-expanded');
    return true;
  }
  desc.classList.add('hski-cky-clamp');
  desc.classList.remove('is-expanded');

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hski-cky-expand';
  btn.textContent = LABEL_EXPAND();
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', ARIA_EXPAND());

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const open = desc.classList.toggle('is-expanded');
    btn.textContent = open ? LABEL_COLLAPSE() : LABEL_EXPAND();
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.setAttribute('aria-label', open ? ARIA_COLLAPSE() : ARIA_EXPAND());
  });

  const group = desc.parentElement;
  const btns = group?.querySelector('.cky-notice-btn-wrapper, [data-cky-tag="notice-buttons"]');
  if (btns && btns.parentElement === group) {
    group.insertBefore(btn, btns);
  } else {
    desc.insertAdjacentElement('afterend', btn);
  }
  return true;
};

const stickyClearancePx = () => {
  const bar = document.querySelector('.booking-mobile-bar:not([hidden])');
  if (!(bar instanceof HTMLElement)) return 0;
  const cs = window.getComputedStyle(bar);
  if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
  const h = bar.getBoundingClientRect().height;
  return h > 0 ? Math.ceil(h) : 0;
};

let lastFabClearance = null;

const syncFabClearance = () => {
  const clear = stickyClearancePx();
  if (clear === lastFabClearance) return;
  lastFabClearance = clear;
  document.documentElement.style.setProperty(
    '--hski-cky-fab-clearance',
    `${clear}px`,
  );
};

const clearFabClearance = () => {
  lastFabClearance = null;
  document.documentElement.style.removeProperty('--hski-cky-fab-clearance');
};

let fabEl = null;

const removeFab = () => {
  fabEl?.remove();
  fabEl = null;
};

const openMobileBanner = () => {
  const root = document.documentElement;
  root.classList.add(CLS_OPEN);
  root.classList.remove(CLS_MIN);
  removeFab();
  const box = consentContainer();
  if (box instanceof HTMLElement) {
    try {
      box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      /* ignore */
    }
  }
};

const ensureFab = () => {
  if (fabEl?.isConnected) {
    syncFabClearance();
    return fabEl;
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hski-cky-fab';
  btn.setAttribute('aria-label', ARIA_FAB());
  btn.setAttribute('title', ARIA_FAB());
  btn.innerHTML =
    '<span class="hski-cky-fab__pulse" aria-hidden="true"></span>' +
    '<svg class="hski-cky-fab__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.7"/>' +
    '<circle cx="9" cy="10" r="1.35" fill="currentColor"/>' +
    '<circle cx="14.2" cy="9.2" r="1.15" fill="currentColor"/>' +
    '<circle cx="10.2" cy="14.5" r="1.2" fill="currentColor"/>' +
    '<circle cx="15" cy="14" r="1.1" fill="currentColor"/>' +
    '</svg>';
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    openMobileBanner();
    // Clamp dopiero gdy baner jest widoczny.
    requestAnimationFrame(() => {
      const desc = descEl(consentContainer());
      if (desc) ensureExpand(desc);
    });
  });
  document.body.appendChild(btn);
  fabEl = btn;
  syncFabClearance();
  return btn;
};

/**
 * Mobile: ukryj pełny baner + pokaż FAB, dopóki user nie otworzy panelu / nie wyrazi zgody.
 * Desktop: zawsze pełny kompaktowy baner (bez FAB).
 */
const syncMobileChrome = () => {
  const root = document.documentElement;

  if (consentCompleted() || !consentContainer()) {
    root.classList.add(CLS_DONE);
    root.classList.remove(CLS_MIN, CLS_OPEN);
    removeFab();
    clearFabClearance();
    return;
  }

  root.classList.remove(CLS_DONE);

  if (!isMobile()) {
    root.classList.remove(CLS_MIN, CLS_OPEN);
    removeFab();
    clearFabClearance();
    return;
  }

  if (root.classList.contains(CLS_OPEN)) {
    removeFab();
    return;
  }

  root.classList.add(CLS_MIN);
  ensureFab();
};

const runClamp = () => {
  const container = consentContainer();
  if (!container) return false;
  // Na mobile w trybie FAB baner jest ukryty — clamp po otwarciu.
  if (
    isMobile() &&
    document.documentElement.classList.contains(CLS_MIN) &&
    !document.documentElement.classList.contains(CLS_OPEN)
  ) {
    return true;
  }
  const desc = descEl(container);
  if (!desc) return false;
  return ensureExpand(desc);
};

let syncing = false;

const run = () => {
  if (syncing) return Boolean(consentContainer()) || consentCompleted();
  syncing = true;
  try {
    syncMobileChrome();
    runClamp();
    return Boolean(consentContainer()) || consentCompleted();
  } finally {
    syncing = false;
  }
};

/** Czy mutacja dotyczy banera CookieYes / naszego FAB (nie Header scroll itd.). */
const mutationRelevant = (mutations) => {
  for (const m of mutations) {
    if (m.type === 'attributes') {
      const t = m.target;
      if (!(t instanceof Element)) continue;
      if (
        t.classList?.contains('cky-consent-container') ||
        t.classList?.contains('cky-consent-bar') ||
        t.classList?.contains('cky-hide') ||
        t.closest?.('.cky-consent-container')
      ) {
        return true;
      }
      continue;
    }
    for (const node of m.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (
        node.classList?.contains('cky-consent-container') ||
        node.classList?.contains('hski-cky-fab') ||
        node.querySelector?.(
          '.cky-consent-container, .cky-consent-bar, .hski-cky-fab',
        )
      ) {
        return true;
      }
    }
  }
  return false;
};

export default function cookieYesCompact() {
  if (typeof document === 'undefined') return;

  let tries = 0;
  let mo = null;
  let raf = 0;
  let settled = false;

  const schedule = () => {
    if (settled || raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      run();
      if (consentCompleted()) {
        settled = true;
        mo?.disconnect();
        mo = null;
      }
    });
  };

  const boot = () => {
    if (run()) {
      if (consentCompleted()) {
        settled = true;
        mo?.disconnect();
        mo = null;
      }
      return;
    }
    if (++tries > 80) return;
    setTimeout(boot, 200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cookieyes_banner_load', () => {
    settled = false;
    schedule();
  });
  document.addEventListener('cookieyes_consent_update', () => {
    document.documentElement.classList.add(CLS_DONE);
    document.documentElement.classList.remove(CLS_MIN, CLS_OPEN);
    removeFab();
    clearFabClearance();
    settled = true;
    mo?.disconnect();
    mo = null;
    run();
  });

  window.matchMedia(MOBILE_MQ).addEventListener('change', () => {
    // Zmiana viewportu: zamknij tryb open, przelicz FAB.
    document.documentElement.classList.remove(CLS_OPEN);
    settled = false;
    schedule();
  });

  window.addEventListener('resize', () => {
    if (fabEl?.isConnected) syncFabClearance();
  });

  // Tylko childList (mount banera / FAB) + class na kontenerze CookieYes — bez style
  // na całym drzewie (Header --nav-height-js / nav-hide = freeze).
  try {
    mo = new MutationObserver((mutations) => {
      if (settled || syncing) return;
      if (!mutationRelevant(mutations)) return;
      schedule();
    });
    mo.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden'],
    });
  } catch {
    /* ignore */
  }

  // Awaria: jeśli JS ukrył baner (early min z head), a FAB nie powstał — przywróć baner.
  window.setTimeout(() => {
    if (
      document.documentElement.classList.contains(CLS_MIN) &&
      !fabEl?.isConnected &&
      consentContainer() &&
      !consentCompleted()
    ) {
      document.documentElement.classList.remove(CLS_MIN);
    }
  }, 6000);
}
